// Magic Wand — creator-side helpers for targeted element edits.
//
// The inspector script inside a generated app (workers/generation-worker/
// src/inspector.ts) speaks protocol v2 with the creator:
//   app → creator: pomela:inspect:ready {v}, pomela:inspect:hover {address,label},
//                  pomela:inspect:select {element}
//   creator → app: pomela:inspect:enable {labels, generic}, :disable, :clear,
//                  :highlight {address}
// These pure helpers validate those messages, build the localized label map
// the inspector paints as its hover chip (labels come from the render-truth
// edit surface — the wand only ever names things it can actually edit), and
// turn a selection + a user instruction into a refinement string the revision
// engine recognises as a targeted edit (REVISION_SYSTEM_PROMPT → "TARGETED EDITS").
import { t } from './i18n';

export interface SelectedElement {
  tag: string;
  classes: string;
  text: string;
  parentTag?: string;
  parentText?: string;
  // §S679 — node ADDRESS from the inspector: [data-pom] identity marker
  // (e.g. 'meta:name'), and/or the clicked row's [data-id] inside its
  // [data-col] collection container. With an address the wand takes the
  // structured edit path; without one it falls back to a targeted AI edit.
  pom?: string;
  collection?: string;
  recordId?: string;
  // §S680 — the friendly, pre-localized label the inspector showed on hover
  // and the snapped address ('pom:…' | 'row:…').
  label?: string;
  address?: string;
  // §node-edit — the POSITIONAL data-node address ('screenId:tag:ordinal') the
  // atlas node-edit layer stamps on editable elements, and a coarse edit `kind`
  // (text/image/button). Present even when `address` (pom:/row:) is empty, so a
  // plain heading/paragraph/image/button still resolves to a concrete address
  // the wand can drive a targeted setNode* op against.
  node?: string;
  kind?: string;
}

/** The concrete address a picked element answers to for a targeted node edit —
 *  the snapped semantic address when it has one, else the positional data-node.
 *  Empty when the element is unaddressable (the wand falls back to an AI edit). */
export function pickedAddress(el: SelectedElement): string {
  return (el.address && el.address.trim()) || (el.node && el.node.trim()) || '';
}

export type NodeEditOp =
  | 'setNodeText'
  | 'setNodeImage'
  | 'setNodeVisibility'
  | 'setNodeStyle'
  | 'setNodeAction';

/**
 * Build a structured node-edit intent for POST /v1/edit (target=draft) from a
 * picked element's address. Element-scoped: the server applies the override to
 * exactly this ONE node (see atlas-v2/node-edit.ts) and rejects a stale/unsafe
 * address honestly. Returns null when there is no address or no usable value —
 * the caller then keeps the current UI (or falls back to a targeted AI edit),
 * never sending an intent that would no-op or fake-succeed.
 */
export function nodeEditIntent(
  address: string,
  op: NodeEditOp,
  arg: { value?: string | boolean; style?: Record<string, string> }
): Record<string, unknown> | null {
  const addr = (address || '').trim();
  if (!addr) return null;
  switch (op) {
    case 'setNodeText': {
      if (typeof arg.value !== 'string') return null;
      return { op, address: addr, value: arg.value.slice(0, 2000) };
    }
    case 'setNodeImage':
    case 'setNodeAction': {
      if (typeof arg.value !== 'string' || !arg.value.trim()) return null;
      return { op, address: addr, value: arg.value.trim() };
    }
    case 'setNodeVisibility': {
      // A boolean (true = hide) or the string forms the server understands.
      const v = arg.value;
      if (typeof v !== 'boolean' && v !== 'hide' && v !== 'show') return null;
      return { op, address: addr, value: v };
    }
    case 'setNodeStyle': {
      if (!arg.style || typeof arg.style !== 'object' || !Object.keys(arg.style).length) return null;
      return { op, address: addr, style: arg.style };
    }
    default:
      return null;
  }
}

/** Parse a seed-row record id ('seed_<collection>_<index>') into an address. */
export function parseSeedRecordId(recordId: string | undefined, collection: string | undefined): { collection: string; rowIndex: number } | null {
  if (!recordId || !collection) return null;
  const m = new RegExp(`^seed_${collection.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\$&')}_(\\d+)$`).exec(recordId);
  if (!m) return null;
  const idx = Number(m[1]);
  return Number.isInteger(idx) && idx >= 0 ? { collection, rowIndex: idx } : null;
}

/**
 * Validate an inbound postMessage as a Magic Wand element selection.
 * Returns the element on success, null for anything unexpected — the
 * creator only ever acts on a well-formed selection.
 */
export function parseInspectorMessage(data: unknown): SelectedElement | null {
  if (!data || typeof data !== 'object') return null;
  const msg = data as { type?: unknown; element?: unknown };
  if (msg.type !== 'pomela:inspect:select') return null;
  const e = msg.element;
  if (!e || typeof e !== 'object') return null;
  const el = e as Record<string, unknown>;
  if (typeof el.tag !== 'string' || !el.tag) return null;
  const s = (v: unknown, cap: number): string | undefined =>
    typeof v === 'string' && v ? v.slice(0, cap) : undefined;
  return {
    tag: el.tag,
    classes: typeof el.classes === 'string' ? el.classes : '',
    text: typeof el.text === 'string' ? el.text : '',
    parentTag: s(el.parentTag, 40),
    parentText: s(el.parentText, 100),
    pom: s(el.pom, 80),
    collection: s(el.collection, 60),
    recordId: s(el.recordId, 80),
    label: s(el.label, 80),
    address: s(el.address, 120),
    node: s(el.node, 120),
    kind: s(el.kind, 12)
  };
}

/** Parse a protocol-v2 ready ack. Returns the version (≥2) or null. */
export function parseInspectorReady(data: unknown): number | null {
  if (!data || typeof data !== 'object') return null;
  const msg = data as { type?: unknown; v?: unknown };
  if (msg.type !== 'pomela:inspect:ready') return null;
  const v = Number(msg.v);
  return Number.isFinite(v) && v >= 2 ? v : null;
}

/** Parse a hover event ({address, label}) — empty address = unaddressed element. */
export function parseInspectorHover(data: unknown): { address: string; label: string } | null {
  if (!data || typeof data !== 'object') return null;
  const msg = data as { type?: unknown; address?: unknown; label?: unknown };
  if (msg.type !== 'pomela:inspect:hover') return null;
  return {
    address: typeof msg.address === 'string' ? msg.address.slice(0, 120) : '',
    label: typeof msg.label === 'string' ? msg.label.slice(0, 80) : ''
  };
}

// Minimal mirror of the wire surface the workspace already holds — kept
// structural so this module stays dependency-light and testable.
interface SurfaceNodeLike {
  id: string;
  kind: string;
  label: string;
  labelHe: string;
  value?: string;
  collection?: string;
  rowIndex?: number;
}

/**
 * Build the address → friendly-label map the inspector paints on hover.
 * Labels are pre-localized here (the inspector is locale-blind) and come
 * from the render-truth surface, so the chip only ever names editable things.
 * Rows are labeled with their own title ("Signature Facial") when known.
 */
export function buildInspectorLabels(nodes: SurfaceNodeLike[], he: boolean): Record<string, string> {
  const out: Record<string, string> = {};
  for (const n of nodes) {
    const label = (he ? n.labelHe : n.label) || n.label;
    if (n.kind === 'identity' || n.kind === 'theme') {
      out[`pom:${n.id}`] = n.value && n.kind === 'identity' ? `${label} · ${n.value.slice(0, 40)}` : label;
    } else if (n.kind === 'seedRow' && n.collection && typeof n.rowIndex === 'number') {
      out[`row:seed_${n.collection}_${n.rowIndex}`] = n.value ? n.value.slice(0, 60) : label;
    }
  }
  return out;
}

/** Localized fallback labels for unaddressed elements, by rough element kind. */
export function genericInspectorLabels(): Record<string, string> {
  return {
    heading: t('ew.pickHeading'),
    text: t('ew.pickText'),
    image: t('ew.pickImage'),
    button: t('ew.pickButton'),
    link: t('ew.pickLink'),
    section: t('ew.pickSection')
  };
}

/**
 * A short human label for the selected element — the inspector's friendly
 * label when it resolved one, otherwise a humanized role + text snippet
 * ('the "Save" button'), never a raw tag name.
 */
export function describeSelection(el: SelectedElement): string {
  if (el.label) return el.label;
  const snippet = el.text ? `"${el.text.slice(0, 32)}${el.text.length > 32 ? '…' : ''}"` : '';
  const kind = /^h[1-6]$/.test(el.tag) ? t('ew.pickHeading')
    : el.tag === 'img' || el.tag === 'video' ? t('ew.pickImage')
    : el.tag === 'button' ? t('ew.pickButton')
    : el.tag === 'a' ? t('ew.pickLink')
    : t('ew.pickText');
  return snippet ? `${kind} · ${snippet}` : kind;
}

/**
 * Format a Magic Wand selection + the user's instruction into a
 * refinement string the revision engine treats as a targeted edit:
 * change ONLY this element, leave everything else byte-identical.
 */
export function formatTargetedEdit(el: SelectedElement, instruction: string): string {
  const lines = [
    'TARGETED EDIT — change ONLY the element described below. Leave every',
    'other element, screen, style, hook and piece of state byte-for-byte',
    'identical to the previous version.',
    '',
    `Element: <${el.tag}>`
  ];
  // §node-edit — the exact rendered-node address, so the revision maps straight
  // to a targeted setNode* op on THIS one element (never a broad rewrite).
  const addr = pickedAddress(el);
  if (addr) lines.push(`Node address: [${addr}]`);
  if (el.classes) lines.push(`Classes: ${el.classes}`);
  if (el.text) lines.push(`Current text: "${el.text}"`);
  if (el.parentTag) {
    lines.push(`Inside: <${el.parentTag}>${el.parentText ? ` ("${el.parentText}")` : ''}`);
  }
  lines.push('', `Requested change: ${instruction.trim()}`);
  return lines.join('\n');
}
